import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Save, Smartphone, Mail, DollarSign, MapPin, Phone, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SettingsManager() {
  const [settings, setSettings] = useState({
    mobile_money_number: '',
    mobile_money_operator: '',
    membership_fee_usd: 5.0,
    contact_email: '',
    contact_phone: '',
    contact_phone_2: '',
    contact_address: '',
    maintenance_mode: false,
    maintenance_message: '',
    bank_iban: '',
    bank_name: '',
    bank_swift: '',
    mobile_money_airtel: '',
    mobile_money_orange: '',
    mobile_money_vodacom: '',
    donation_in_kind_contact: '',
    donation_services_contact: '',
    crowdfunding_globalgiving: '',
    crowdfunding_helloasso: '',
    crowdfunding_gofundme: '',
    crowdfunding_ulule: '',
    crowdfunding_other_name: '',
    crowdfunding_other_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, { withCredentials: true });
      setSettings(response.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        ...settings,
        membership_fee_usd: parseFloat(settings.membership_fee_usd)
      }, { withCredentials: true });
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>;
  }

  return (
    <div data-testid="settings-manager">
      <h2 className="text-2xl font-bold text-[#2E332D] mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        Paramètres du Site
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Mobile Money Settings */}
        <div className="card">
          <h3 className="text-lg font-bold text-[#2E332D] mb-4 flex items-center gap-2">
            <Smartphone className="text-[#4A6B48]" size={20} />
            Mobile Money - Frais d&apos;Adhésion
          </h3>
          <p className="text-sm text-[#5C665A] mb-4">
            Ces informations seront visibles par les membres pour payer leurs frais d&apos;adhésion annuels.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2E332D] mb-2">
                Opérateur Mobile Money *
              </label>
              <select
                value={settings.mobile_money_operator}
                onChange={(e) => setSettings({...settings, mobile_money_operator: e.target.value})}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="mm-operator-select"
              >
                <option value="">-- Sélectionner --</option>
                <option value="M-Pesa">M-Pesa (Vodacom)</option>
                <option value="Airtel Money">Airtel Money</option>
                <option value="Orange Money">Orange Money</option>
                <option value="Africell Money">Africell Money</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2E332D] mb-2">
                Numéro Mobile Money *
              </label>
              <input
                type="tel"
                placeholder="+243 XXX XXX XXX"
                value={settings.mobile_money_number}
                onChange={(e) => setSettings({...settings, mobile_money_number: e.target.value})}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="mm-number-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2E332D] mb-2">
                Frais d&apos;adhésion annuels (USD)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C665A]" size={16} />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={settings.membership_fee_usd}
                  onChange={(e) => setSettings({...settings, membership_fee_usd: e.target.value})}
                  className="w-full pl-10 pr-4 py-2 border border-[#E5E3DB] rounded-lg"
                  data-testid="membership-fee-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Contact Settings */}
        <div className="card">
          <h3 className="text-lg font-bold text-[#2E332D] mb-4 flex items-center gap-2">
            <Mail className="text-[#4A6B48]" size={20} />
            Coordonnées de Contact
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2E332D] mb-2">Email de contact</label>
              <input
                type="email"
                placeholder="contact@cresa-asbl.org"
                value={settings.contact_email}
                onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="settings-contact-email"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2E332D] mb-2 inline-flex items-center gap-1">
                  <Phone size={14} /> Téléphone principal
                </label>
                <input
                  type="tel"
                  placeholder="+243 853 717 311"
                  value={settings.contact_phone}
                  onChange={(e) => setSettings({...settings, contact_phone: e.target.value})}
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                  data-testid="settings-contact-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2E332D] mb-2 inline-flex items-center gap-1">
                  <Phone size={14} /> Téléphone secondaire
                </label>
                <input
                  type="tel"
                  placeholder="+243 847 143 388"
                  value={settings.contact_phone_2 || ''}
                  onChange={(e) => setSettings({...settings, contact_phone_2: e.target.value})}
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                  data-testid="settings-contact-phone-2"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2E332D] mb-2 inline-flex items-center gap-1">
                <MapPin size={14} /> Adresse physique
              </label>
              <input
                type="text"
                placeholder="Avenue des Martyrs ex-VAMARO N°05, Bukavu, RD Congo"
                value={settings.contact_address || ''}
                onChange={(e) => setSettings({...settings, contact_address: e.target.value})}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="settings-contact-address"
              />
            </div>
          </div>
        </div>

        {/* Maintenance Mode */}
        <div className="card border-l-4 border-[#D87D4A]">
          <h3 className="text-lg font-bold text-[#2E332D] mb-2 flex items-center gap-2">
            <Wrench className="text-[#D87D4A]" size={20} />
            Mode maintenance
          </h3>
          <p className="text-sm text-[#5C665A] mb-4">
            Activer ce mode bloque l&apos;accès public au site. Les administrateurs gardent l&apos;accès via <code>/login</code>.
          </p>

          <label className="inline-flex items-center gap-3 cursor-pointer mb-4" data-testid="settings-maintenance-toggle">
            <input
              type="checkbox"
              checked={!!settings.maintenance_mode}
              onChange={(e) => setSettings({...settings, maintenance_mode: e.target.checked})}
              className="w-5 h-5 rounded border-[#E5E3DB] accent-[#D87D4A]"
            />
            <span className={`text-sm font-bold ${settings.maintenance_mode ? 'text-[#D87D4A]' : 'text-[#5C665A]'}`}>
              {settings.maintenance_mode ? '🔒 Site fermé au public' : '🌐 Site ouvert au public'}
            </span>
          </label>

          <div>
            <label className="block text-sm font-medium text-[#2E332D] mb-2">Message affiché aux visiteurs</label>
            <textarea
              rows={2}
              placeholder="Le site est temporairement indisponible pour maintenance. Merci de revenir bientôt."
              value={settings.maintenance_message || ''}
              onChange={(e) => setSettings({...settings, maintenance_message: e.target.value})}
              className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
              data-testid="settings-maintenance-message"
            />
          </div>
        </div>

        {/* Donation channels Settings */}
        <div className="card">
          <h3 className="text-lg font-bold text-[#2E332D] mb-4 flex items-center gap-2">
            <Smartphone className="text-[#D87D4A]" size={20} />
            Modalités de don (affichées sur /dons)
          </h3>
          <p className="text-sm text-[#5C665A] mb-4">
            Renseignez vos numéros Mobile Money, IBAN bancaire et contacts pour les dons en nature/services.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-[#2E332D] mb-1">Mobile Money — Airtel</label>
              <input type="tel" placeholder="+243 XXX XXX XXX" value={settings.mobile_money_airtel || ''}
                onChange={(e) => setSettings({...settings, mobile_money_airtel: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm"
                data-testid="mm-airtel-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#2E332D] mb-1">Mobile Money — Orange</label>
              <input type="tel" placeholder="+243 XXX XXX XXX" value={settings.mobile_money_orange || ''}
                onChange={(e) => setSettings({...settings, mobile_money_orange: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm"
                data-testid="mm-orange-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#2E332D] mb-1">Mobile Money — Vodacom (M-Pesa)</label>
              <input type="tel" placeholder="+243 XXX XXX XXX" value={settings.mobile_money_vodacom || ''}
                onChange={(e) => setSettings({...settings, mobile_money_vodacom: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm"
                data-testid="mm-vodacom-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#2E332D] mb-1">Nom de la banque</label>
              <input type="text" placeholder="Ex: Rawbank, Equity, ..." value={settings.bank_name || ''}
                onChange={(e) => setSettings({...settings, bank_name: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-[#2E332D] mb-1">IBAN / N° de compte bancaire</label>
              <input type="text" placeholder="CD XX XXXXX XXXXX XXXXX XX" value={settings.bank_iban || ''}
                onChange={(e) => setSettings({...settings, bank_iban: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm font-mono"
                data-testid="bank-iban-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#2E332D] mb-1">SWIFT/BIC (international)</label>
              <input type="text" placeholder="Ex: RAWBCDKIN" value={settings.bank_swift || ''}
                onChange={(e) => setSettings({...settings, bank_swift: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm font-mono" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#2E332D] mb-1">Contact dons en nature</label>
              <input type="text" placeholder="téléphone ou email" value={settings.donation_in_kind_contact || ''}
                onChange={(e) => setSettings({...settings, donation_in_kind_contact: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-[#2E332D] mb-1">Contact dons en services / bénévolat</label>
              <input type="text" placeholder="téléphone ou email" value={settings.donation_services_contact || ''}
                onChange={(e) => setSettings({...settings, donation_services_contact: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" />
            </div>
          </div>
        </div>

        {/* Crowdfunding platforms */}
        <div className="card">
          <h3 className="text-lg font-bold text-[#2E332D] mb-4 flex items-center gap-2">
            <Smartphone className="text-[#D87D4A]" size={20} />
            Plateformes de financement participatif (crowdfunding)
          </h3>
          <p className="text-sm text-[#5C665A] mb-4">
            Collez les URLs de vos pages CRESA sur les plateformes de collecte de fonds alignées avec notre modèle.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-[#2E332D] mb-1">GlobalGiving (ONG internationales)</label>
              <input type="url" placeholder="https://www.globalgiving.org/projects/..." value={settings.crowdfunding_globalgiving || ''}
                onChange={(e) => setSettings({...settings, crowdfunding_globalgiving: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#2E332D] mb-1">HelloAsso (asso française, sans commission)</label>
              <input type="url" placeholder="https://www.helloasso.com/associations/..." value={settings.crowdfunding_helloasso || ''}
                onChange={(e) => setSettings({...settings, crowdfunding_helloasso: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#2E332D] mb-1">GoFundMe</label>
              <input type="url" placeholder="https://www.gofundme.com/f/..." value={settings.crowdfunding_gofundme || ''}
                onChange={(e) => setSettings({...settings, crowdfunding_gofundme: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#2E332D] mb-1">Ulule (projets communautaires)</label>
              <input type="url" placeholder="https://fr.ulule.com/..." value={settings.crowdfunding_ulule || ''}
                onChange={(e) => setSettings({...settings, crowdfunding_ulule: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#2E332D] mb-1">Autre — nom</label>
              <input type="text" placeholder="Ex: BetterPlace, Catarse..." value={settings.crowdfunding_other_name || ''}
                onChange={(e) => setSettings({...settings, crowdfunding_other_name: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#2E332D] mb-1">Autre — URL</label>
              <input type="url" placeholder="https://..." value={settings.crowdfunding_other_url || ''}
                onChange={(e) => setSettings({...settings, crowdfunding_other_url: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" />
            </div>
          </div>
        </div>



        <button type="submit" disabled={saving} className="btn-primary inline-flex items-center disabled:opacity-50" data-testid="save-settings-button">
          <Save size={18} className="mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
        </button>
      </form>
    </div>
  );
}
